import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, ArrowRight, Award, Star, Globe2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import SEO from '../components/SEO';
import {
  personSchema, organizationSchema, websiteSchema, localBusinessSchema,
  VIVEK_ID, VIVEK_URL, SITE_URL,
} from '../utils/schemas';

const EXPERTS = [
  {
    slug: 'vivek-virat-mudgal',
    name: 'Vivek Virat Mudgal',
    role: 'Founder · Vedic Astrologer',
    tagline: 'Vedic Astrology · Parashari Jyotish · Vastu',
    photo: '/media/founder/IMG_9620-removebg-preview.png',
    location: 'Faridabad, India',
    years: '33+ years',
    highlights: ['Founder of ViratAstro', 'Featured on News Nation', '33,000+ clients · 14+ countries'],
    cta: 'View Profile',
    isFounder: true,
    // Image-fit strategy: the founder photo is a transparent-background portrait
    // PNG (removebg). We use object-cover + object-top so the head/face stays
    // in-frame while the card fills fully like Priya's (was previously stretched
    // via a mis-placed background-class in `fit` — see git blame Feb 2026).
    fit: 'object-cover object-top',
    // Card-level backdrop so the removebg PNG has visual context (previously
    // this gradient was accidentally applied to <img> via the fit field).
    cardBg: 'bg-gradient-to-br from-gold/12 via-bg-secondary to-purple/15',
  },
  {
    slug: 'priya-khurana',
    name: 'Priya Khurana',
    role: 'Featured Tarot Expert',
    tagline: 'Tarot · Relationship Coach · Self-Awareness Mentor',
    photo: '/experts/priya-khurana-card.png',
    location: 'Dubai, UAE',
    years: '6+ years',
    highlights: ['Advanced training Canada + Singapore', 'Online consultations worldwide', 'English-market specialist'],
    cta: 'View Profile',
    isFounder: false,
    fit: 'object-cover',
    cardBg: '',
  },
];

const expertDirectorySchemas = () => [
  personSchema(),
  organizationSchema(),
  websiteSchema(),
  {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Experts at ViratAstro',
    description: 'Meet the trusted experts at ViratAstro — Vedic Astrologer Vivek Virat Mudgal and Featured Tarot Expert Priya Khurana.',
    url: `${SITE_URL}/experts`,
    publisher: { '@id': `${SITE_URL}#organization` },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: EXPERTS.map((e, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `${SITE_URL}/experts/${e.slug}`,
        name: e.name,
      })),
    },
  },
];

export default function ExpertsDirectoryPage() {
  return (
    <div className="min-h-screen bg-bg-primary text-text-primary">
      <SEO
        title="Experts at ViratAstro — Vivek Mudgal, Priya Khurana"
        description="Meet the trusted experts at ViratAstro: Founder Acharya Vivek Virat Mudgal (Vedic Astrologer, 33+ years, Faridabad) and Featured Tarot Expert Priya Khurana (Dubai-based, 6+ years, Tarot & Relationship Coaching)."
        path="/experts"
        keywords="Experts ViratAstro, Vivek Mudgal Astrologer, Priya Khurana Tarot, Best Astrologer India, Tarot Reader Dubai, Vedic Expert, Relationship Coach UAE"
        jsonLd={expertDirectorySchemas()}
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Experts', path: '/experts' }]}
      />

      <Header />

      <section className="pt-28 pb-12">
        <div className="container mx-auto px-4 max-w-6xl text-center">
          <span className="text-xs tracking-[0.3em] text-gold/70 uppercase">Expert Directory</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mt-2 mb-3">
            Meet ViratAstro's Experts
          </h1>
          <p className="text-text-muted max-w-2xl mx-auto">
            From classical Parashari Jyotish to modern Tarot & Relationship Coaching — our curated experts cover the full spectrum of spiritual guidance.
          </p>
        </div>
      </section>

      <section className="pb-20">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-6">
            {EXPERTS.map((e) => (
              <Link
                key={e.slug}
                to={`/experts/${e.slug}`}
                className="group block rounded-3xl bg-bg-card border border-gold/30 hover:border-gold/70 overflow-hidden transition-all hover:shadow-[0_0_40px_rgba(255,215,0,0.18)]"
                data-testid={`expert-card-${e.slug}`}
              >
                <div className={`aspect-[4/3] relative overflow-hidden ${e.cardBg || ''}`}>
                  <img
                    src={e.photo}
                    alt={`${e.name} — ${e.role}`}
                    className={`absolute inset-0 w-full h-full ${e.fit} group-hover:scale-105 transition-transform duration-700`}
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-bg-primary/95 via-bg-primary/30 to-transparent" />
                  {e.isFounder && (
                    <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-gold text-bg-primary text-[10px] font-bold tracking-[0.25em] uppercase">
                      ⭐ Founder
                    </div>
                  )}
                  <div className="absolute bottom-4 left-4 right-4">
                    <h2 className="text-2xl font-heading font-bold text-text-primary mb-1">{e.name}</h2>
                    <p className="text-sm text-gold">{e.role}</p>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-sm text-text-muted leading-relaxed mb-4">{e.tagline}</p>
                  <div className="flex flex-wrap gap-3 text-[11px] text-text-muted mb-5">
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-gold" />{e.location}</span>
                    <span className="flex items-center gap-1"><Award className="w-3 h-3 text-gold" />{e.years}</span>
                  </div>
                  <ul className="space-y-1 mb-5 text-xs text-text-muted">
                    {e.highlights.map((h) => <li key={h}>✓ {h}</li>)}
                  </ul>
                  <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold text-bg-primary font-semibold text-sm group-hover:bg-yellow-400 transition">
                    {e.cta} <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Internal linking — Experts ↔ Services */}
      <section className="py-10 bg-bg-secondary">
        <div className="container mx-auto px-4 max-w-5xl text-center">
          <p className="text-xs tracking-[0.3em] text-gold/70 uppercase mb-3">Explore Services</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/kundli" className="px-5 py-2 rounded-full border border-gold/30 text-sm text-gold hover:bg-gold/10 transition">Free Kundli</Link>
            <Link to="/poocho-guruji" className="px-5 py-2 rounded-full border border-gold/30 text-sm text-gold hover:bg-gold/10 transition">Pucho Guruji</Link>
            <Link to="/daily-tarot" className="px-5 py-2 rounded-full border border-gold/30 text-sm text-gold hover:bg-gold/10 transition">Daily Tarot</Link>
            <Link to="/daily-rashifal" className="px-5 py-2 rounded-full border border-gold/30 text-sm text-gold hover:bg-gold/10 transition">Daily Rashifal</Link>
            <Link to="/upay" className="px-5 py-2 rounded-full border border-gold/30 text-sm text-gold hover:bg-gold/10 transition">Vedic Upay</Link>
            <Link to="/blog" className="px-5 py-2 rounded-full border border-gold/30 text-sm text-gold hover:bg-gold/10 transition">Articles</Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingActions />
    </div>
  );
}
